import { SwapCalls, ArrowBack } from '@mui/icons-material';
import { Grid, Stack, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router';

import csImage from 'assets/images/svg/conditional-split-preview.svg';
import ssImage from 'assets/images/svg/single-send-preview.svg';
import tdImage from 'assets/images/svg/time-delay-preview.svg';
import { getCampaignRoute } from 'domains/campaigns/state/utils';
import { CampaignChannels, CampaignKey } from 'domains/campaigns/types';
import Button from 'domains/core/components/Button';
import UpdateTag from 'domains/core/components/UpdateTag';
import { trackCuredEvent, MixpanelEvent, MixpanelFlow } from 'helpers/initMixpanel';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import { URLPaths } from 'models/enums';

import { channelIcons } from '../../CurationsPage/utils';

import CurationItem from './CurationItem';

// Large SVG loaded as external asset to avoid BABEL processing
const customImage = '/assets/images/svg/custom-curation-preview.svg';

// Styled component for channel icons
const StyledChannelIcon = {
    fontSize: 101,
    color: 'action.active',
    paddingTop: '36px',
};

const gridSizes = { xs: 12, md: 6, xl: 3 };
const accessControlGridSizes = { xs: 12, sm: 6, md: 3 };

const CurationLandingPage = () => {
    const history = useHistory();
    const { accessControlLayer: accessControlLayerFlag } = useFlags();
    const { data: featureSettings, isSuccess: isFeatureSettingsSuccess } = useFeatureSettings();

    const hasEmailCurations = isFeatureSettingsSuccess && featureSettings?.email_enabled !== false;
    const hasDirectMailCurations = isFeatureSettingsSuccess && featureSettings?.directmail_enabled; // directmail_enabled is opt-in; undefined means disabled
    const hasSMSCurations = isFeatureSettingsSuccess && featureSettings?.sms_enabled !== false;
    const hasVoiceCurations = isFeatureSettingsSuccess && featureSettings?.voice_enabled;
    const hasAccessControlLayer = isFeatureSettingsSuccess && featureSettings?.acl_enabled && accessControlLayerFlag;

    const routeSingleSend = getCampaignRoute({ campaignKey: CampaignKey.SINGLE_SEND });
    const routeEmailSeries = getCampaignRoute({ campaignKey: CampaignKey.EMAIL_SERIES });

    const singleSendImage = <img src={ssImage} alt="Single Send campaign flow preview" width="100%" />;
    const timeDelayImage = <img src={tdImage} alt="Email Series campaign flow preview" width="100%" />;
    const conditionalSplitImage = <img src={csImage} alt="Conditional Split campaign flow preview" width="100%" />;
    const customCurationImage = <img src={customImage} alt="Custom Curation campaign flow preview" width="100%" />;

    const renderSeparateChannelGrids = () => {
        const EmailIcon = channelIcons[CampaignChannels.EMAIL];
        const SMSIcon = channelIcons[CampaignChannels.SMS];
        const VoiceIcon = channelIcons[CampaignChannels.VOICE_CALL];
        const DirectMailIcon = channelIcons[CampaignChannels.DIRECT_MAIL];

        return (
            <>
                {/* Email Grid */}
                {hasEmailCurations && (
                    <Grid item {...accessControlGridSizes}>
                        <CurationItem
                            imageSrc={
                                <Stack alignItems="center" justifyContent="center">
                                    <EmailIcon sx={StyledChannelIcon} />
                                </Stack>
                            }
                            title="Email"
                            isAccessControlLayer={hasAccessControlLayer}
                            onClick={() => {
                                trackCuredEvent(
                                    MixpanelEvent.NEW_CAMPAIGN_START,
                                    { channel: CampaignChannels.EMAIL },
                                    MixpanelFlow.CAMPAIGN
                                );
                                history.push({
                                    pathname: URLPaths.CURATIONS_CUSTOM,
                                    state: { channel: CampaignChannels.EMAIL },
                                });
                            }}
                        />
                    </Grid>
                )}

                {/* SMS Grid */}
                {hasSMSCurations && (
                    <Grid item {...accessControlGridSizes}>
                        <CurationItem
                            imageSrc={
                                <Stack alignItems="center" justifyContent="center">
                                    <SMSIcon sx={StyledChannelIcon} />
                                </Stack>
                            }
                            title="Text Message"
                            isAccessControlLayer={hasAccessControlLayer}
                            onClick={() => {
                                trackCuredEvent(
                                    MixpanelEvent.NEW_CAMPAIGN_START,
                                    { channel: CampaignChannels.SMS },
                                    MixpanelFlow.CAMPAIGN
                                );
                                history.push({
                                    pathname: URLPaths.CURATIONS_CUSTOM,
                                    state: { channel: CampaignChannels.SMS },
                                });
                            }}
                        />
                    </Grid>
                )}

                {/* Voice Grid */}
                {hasVoiceCurations && (
                    <Grid item {...accessControlGridSizes}>
                        <CurationItem
                            imageSrc={
                                <Stack alignItems="center" justifyContent="center">
                                    <VoiceIcon sx={StyledChannelIcon} />
                                </Stack>
                            }
                            title="Voice Call"
                            isAccessControlLayer={hasAccessControlLayer}
                            onClick={() => {
                                trackCuredEvent(
                                    MixpanelEvent.NEW_CAMPAIGN_START,
                                    { channel: CampaignChannels.VOICE_CALL },
                                    MixpanelFlow.CAMPAIGN
                                );
                                history.push({
                                    pathname: URLPaths.CURATIONS_CUSTOM,
                                    state: { channel: CampaignChannels.VOICE_CALL },
                                });
                            }}
                        />
                    </Grid>
                )}

                {/* Direct Mail Grid */}
                {hasDirectMailCurations && (
                    <Grid item {...accessControlGridSizes}>
                        <CurationItem
                            imageSrc={
                                <Stack alignItems="center" justifyContent="center">
                                    <DirectMailIcon sx={StyledChannelIcon} />
                                </Stack>
                            }
                            title="Direct Mail"
                            isAccessControlLayer={hasAccessControlLayer}
                            onClick={() => {
                                trackCuredEvent(
                                    MixpanelEvent.NEW_CAMPAIGN_START,
                                    { channel: CampaignChannels.DIRECT_MAIL },
                                    MixpanelFlow.CAMPAIGN
                                );
                                history.push({
                                    pathname: URLPaths.CURATIONS_CUSTOM,
                                    state: { channel: CampaignChannels.DIRECT_MAIL },
                                });
                            }}
                        />
                    </Grid>
                )}
            </>
        );
    };

    const renderCustomCurationOption = () => {
        const sharedProps = {
            imageSrc: customCurationImage,
            subtitle: 'Build your own communication flows',
            title: 'Custom Curation',
        };
        const defaultChannel = hasEmailCurations
            ? CampaignChannels.EMAIL
            : hasSMSCurations
            ? CampaignChannels.SMS
            : hasVoiceCurations
            ? CampaignChannels.VOICE_CALL
            : CampaignChannels.DIRECT_MAIL;

        const navigateToCustomCurationsPage = () => {
            trackCuredEvent(MixpanelEvent.NEW_CAMPAIGN_START, { channel: defaultChannel }, MixpanelFlow.CAMPAIGN);
            history.push({
                pathname: URLPaths.CURATIONS_CUSTOM,
                state: { channel: defaultChannel },
            });
        };

        const enabledChannelCount = [
            hasEmailCurations,
            hasSMSCurations,
            hasVoiceCurations,
            hasDirectMailCurations,
        ].filter(Boolean).length;
        const shouldRenderCustomOptions = enabledChannelCount > 1;

        if (shouldRenderCustomOptions) {
            return (
                <Grid item {...gridSizes}>
                    <CurationItem
                        options={[
                            ...(hasEmailCurations
                                ? [
                                      {
                                          label: 'Email',
                                          onClick: navigateToCustomCurationsPage,
                                      },
                                  ]
                                : []),
                            ...(hasSMSCurations
                                ? [
                                      {
                                          label: 'Text Message',
                                          onClick: () => {
                                              trackCuredEvent(
                                                  MixpanelEvent.NEW_CAMPAIGN_START,
                                                  { channel: CampaignChannels.SMS },
                                                  MixpanelFlow.CAMPAIGN
                                              );
                                              history.push({
                                                  pathname: URLPaths.CURATIONS_CUSTOM,
                                                  state: { channel: CampaignChannels.SMS },
                                              });
                                          },
                                      },
                                  ]
                                : []),
                            ...(hasVoiceCurations
                                ? [
                                      {
                                          label: 'Voice Call',
                                          onClick: () => {
                                              trackCuredEvent(
                                                  MixpanelEvent.NEW_CAMPAIGN_START,
                                                  { channel: CampaignChannels.VOICE_CALL },
                                                  MixpanelFlow.CAMPAIGN
                                              );
                                              history.push({
                                                  pathname: URLPaths.CURATIONS_CUSTOM,
                                                  state: { channel: CampaignChannels.VOICE_CALL },
                                              });
                                          },
                                      },
                                  ]
                                : []),
                            ...(hasDirectMailCurations
                                ? [
                                      {
                                          label: 'Direct Mail',
                                          onClick: () => {
                                              trackCuredEvent(
                                                  MixpanelEvent.NEW_CAMPAIGN_START,
                                                  { channel: CampaignChannels.DIRECT_MAIL },
                                                  MixpanelFlow.CAMPAIGN
                                              );
                                              history.push({
                                                  pathname: URLPaths.CURATIONS_CUSTOM,
                                                  state: { channel: CampaignChannels.DIRECT_MAIL },
                                              });
                                          },
                                      },
                                  ]
                                : []),
                        ]}
                        {...sharedProps}
                    />
                </Grid>
            );
        }

        if (enabledChannelCount === 0) {
            return null;
        }

        return (
            <Grid item {...gridSizes}>
                <CurationItem onClick={navigateToCustomCurationsPage} {...sharedProps} />
            </Grid>
        );
    };

    return (
        <>
            <Grid container direction="column" my={8}>
                <Grid container direction="column">
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="end" columnGap={0.5}>
                            <SwapCalls fontSize="medium" color="action" />
                            <Typography variant="caption" color="primary.main">
                                curations
                            </Typography>
                        </Stack>
                        <Button
                            variant="text"
                            startIcon={<ArrowBack />}
                            onClick={() => history.push(URLPaths.CURATIONS)}
                        >
                            Back
                        </Button>
                    </Stack>
                    <Stack direction="row" alignItems="center" columnGap={2} mb={2}>
                        <Typography variant="h3">create better connections</Typography>
                        <UpdateTag
                            tag="Feature Update - Curations"
                            header="What's Changing"
                            body={
                                <Stack rowGap={2}>
                                    <Typography variant="body2">
                                        We're streamlining the way you create. To ensure you have the most powerful,
                                        flexible tools at your fingertips, we are officially retiring Structured Layouts
                                        at the end of Q2. Our Custom Curation Builder has become the primary home for
                                        our latest features, performance updates, and design flexibility. By moving away
                                        from legacy structured formats, we can focus 100% of our energy on making the
                                        custom builder the best experience possible for you.
                                    </Typography>
                                    <Typography variant="h3">What it means for you</Typography>
                                    <Stack component="ul" rowGap={1} sx={{ pl: 2, m: 0 }}>
                                        <Typography component="li" variant="body2">
                                            Any campaigns currently using structured layouts will remain active, even
                                            after the layouts are retired.
                                        </Typography>
                                        <Typography component="li" variant="body2">
                                            Users should start using custom curation builder as their primary campaign
                                            builder.
                                        </Typography>
                                    </Stack>
                                </Stack>
                            }
                        />
                    </Stack>
                </Grid>
                <Grid container>
                    <Grid item>
                        <Typography>
                            Select a Curation below to easily reach your customers at the right time with the right
                            content.
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container spacing={4} pb={4}>
                {hasAccessControlLayer ? (
                    // Show separate channel grids when access control layer is enabled
                    renderSeparateChannelGrids()
                ) : (
                    // Show current structure when access control layer is disabled
                    <>
                        {renderCustomCurationOption()}
                        {hasEmailCurations && (
                            <Grid item {...gridSizes}>
                                <CurationItem
                                    imageSrc={singleSendImage}
                                    subtitle="Quick and direct communication with individuals"
                                    title="Single Send"
                                    onClick={() => {
                                        history.push(routeSingleSend);
                                    }}
                                />
                            </Grid>
                        )}
                        {hasEmailCurations && (
                            <Grid item {...gridSizes}>
                                <CurationItem
                                    imageSrc={timeDelayImage}
                                    subtitle="Connected sends to encourage engagement"
                                    title="Email Series"
                                    onClick={() => {
                                        history.push(routeEmailSeries);
                                    }}
                                />
                            </Grid>
                        )}
                        {hasEmailCurations && (
                            <Grid item {...gridSizes}>
                                <CurationItem
                                    imageSrc={conditionalSplitImage}
                                    subtitle="Focus communication based on engagement"
                                    title="Conditional Split"
                                    onClick={() => {
                                        history.push(URLPaths.CURATIONS_CONDITIONAL_SPLIT);
                                    }}
                                />
                            </Grid>
                        )}
                    </>
                )}
            </Grid>
        </>
    );
};

export default CurationLandingPage;
